import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Page hero used on the Commercial page. Visually identical to the standard
 * PageHero banner but ships with the Capacity Commercial aerial flyer image
 * as the default background so the page looks right out of the box. Both the
 * title and the background image remain editor-overridable.
 */

// NOTE: the image URL below is also encoded in `contentSchema.slotGroups`
// below. The schema is extracted statically (eval of the literal), so the URL
// must be inlined there too — keep the two in sync.
const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=png/property/6767906449167749/01a0dabd-d9b5-71fe-b14a-264c7f20a379.webp',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue:
        'Aerial view of The Heights at Happy Valley with available retail suites outlined',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function CommercialHero() {
  const title = useContentToken('commercialhero.title');
  const bg = useSlotGroup('commercialhero.bg_image', bgImageDef);

  return (
    <section className="relative h-[30vh] min-h-[200px] max-h-[350px] overflow-hidden">
      <EditableSlotGroup
        groupPath="commercialhero.bg_image"
        definition={bgImageDef}
        className="absolute inset-0"
      >
        {bg.image ? (
          <VibesitesImage
            url={bg.image}
            alt={bg.alt || 'Commercial space hero'}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[var(--vibe-color-text)]" />
        )}
        <div className="absolute inset-0 bg-black/40" />
      </EditableSlotGroup>

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center text-white">
        <EditableToken
          path="commercialhero.title"
          as="h1"
          /* max-w constraint on md & lg forces a 2-line break at tablet /
             smaller-desktop widths, then releases at xl+ so wide screens get
             the single-line treatment. `[text-wrap:balance]` lets browsers
             even out the two lines once wrapped. */
          className="mb-3 font-heading text-3xl font-normal uppercase tracking-wider sm:text-4xl md:text-5xl md:max-w-md xl:max-w-none [text-wrap:balance]"
        >
          {title || 'Commercial Space for Lease'}
        </EditableToken>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CommercialHero',
  displayName: 'Commercial Hero',
  tokens: {
    'commercialhero.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Commercial Space for Lease',
    },
  },
  slotGroups: {
    'commercialhero.bg_image': bgImageDef,
  },
};

(CommercialHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CommercialHero;
